import * as THREE from 'three';
import type { Viewer } from '../../../viewer/viewer';
import type { Viewer as ViewerType } from '../../../viewer/viewer';
import type { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { getSurfaceKindProperty, getAreaPointsProperty } from '@pointorama/potree-shared';
import type { Subscription } from '@pointorama/potree-shared';
import { SurfaceKind } from '../../../utils/PointoramaSurface';
import { AreaAnnotation, OpeningAnnotation } from '../../../utils/PointoramaAnnotation';
import type { AnnotationKindAdapter, CommitContext, HitTestContext } from './line-tool/AnnotationKindAdapter';
import type { EditableAnnotation } from '../modules/MeasureEngine';
import type { EditableShape } from './line-tool/EditableShape';
import type { BasePlaneState, SelectionContextProvider } from './line-tool/SelectionContextProvider';
import { SurfaceSelectionContext } from './line-tool/SurfaceSelectionContext';

/**
 * Kind adapter for the LineTool registration that draws SURFACE annotations.
 * Replaces the old monolithic `AutoSurfaceTool`: the shared LineTool now owns
 * prompt drawing, segmentation, box-extrude, and view tabs, while this adapter
 * supplies the surface-specific bits (pipeline configs, hit-test, commit, plane
 * frame).
 *
 * Plane handling: the `SurfaceHostTool` (a `SurfaceToolBase`) installs the
 * selected surface's plane onto `viewer.viewerMode.associatedPlane` and aligns
 * the camera to it. The LineTool's `CoordinateTransformer.screenToWorld`
 * unprojects onto that plane, so prompts + segmentation captures already
 * produce surface-plane-aligned world coordinates; `snapPolygonToBasePlane`
 * removes residual sub-cm noise by projecting onto the plane in 3D.
 *
 * Pipeline switching: surfaces come in two kinds (SURFACE / SECTION) that want
 * different polygon pipelines. The kind lives on a reactive property keyed by
 * the selected surface's identifier, so this adapter tracks the selected
 * surface (via its `SurfaceSelectionContext`) and exposes the right config path
 * through the `polygonPipelineConfigPath` getter, notifying the host through
 * `onPipelineConfigChanged` when the kind flips so the worker reloads.
 */
export class SurfaceKindAdapter implements AnnotationKindAdapter {
  /** Draw-only: matches no scene shape, so the engine edits nothing. */
  readonly editDomain = 'surface' as const;

  /** Polygon pipeline path for the SURFACE kind. */
  private static readonly SURFACE_PIPELINE_CONFIG = '/config/segmentation/surface-poly-pipeline-config.json';
  /** Polygon pipeline path for the SECTION kind. */
  private static readonly SECTION_PIPELINE_CONFIG = '/config/segmentation/section-poly-pipeline-config.json';

  /**
   * The surface selection context this adapter owns. Built once in
   * `createSelectionContext`; the commit / hit-test / plane-frame paths read
   * the active surface plane from `viewer.viewerMode.associatedPlane`, but the
   * pipeline-switch path reads the selected surface off this context.
   */
  private surfaceContext: SurfaceSelectionContext | null = null;

  /** Active surface kind, driving the polygon pipeline path getter. */
  private currentSurfaceKind: SurfaceKind = SurfaceKind.SURFACE;

  /** Subscription to the selected surface's kind reactive property. */
  private surfaceKindSubscription: Subscription | null = null;

  /** Identifier the current `surfaceKindSubscription` is bound to. */
  private subscribedSurfaceIdentifier: string | null = null;

  /** Host-supplied callback: reload the worker pipeline when the kind flips. */
  private onPipelineConfigChanged: (() => void) | null = null;

  /**
   * Surfaces are freeform polygons (like rooms), not axis-locked rectangles
   * like door/window openings — so 90° snap stays opt-in (shift ENABLES it).
   */
  readonly invertShiftFor90Snap = false;

  /**
   * Surfaces let the user tweak the polygon (prompt handles / box) between
   * region selection and confirm, matching the old AutoSurfaceTool UX — so the
   * new-shape flow does NOT auto-commit straight out of the region.
   */
  readonly autoCommitNewFromRegion = false;

  createSelectionContext(viewer: ViewerType, persona: IPersona): SelectionContextProvider {
    this.surfaceContext = new SurfaceSelectionContext(viewer, persona);
    // Bind to whatever surface is selected right now (may be null).
    this.syncSurfaceKindSubscription();
    return this.surfaceContext;
  }

  /**
   * Host hook: called whenever the selected surface changes (the host already
   * gets the `surface_selection_changed` signal) so the adapter can rebind the
   * kind subscription to the newly selected surface.
   */
  refreshForSelection(): void {
    this.syncSurfaceKindSubscription();
  }

  /**
   * Rebind the surface-kind subscription to the currently selected surface.
   * Mirrors `AutoSurfaceTool.subscribeToSurfaceKind`: reads the surface's
   * current `surfaceKind` for the initial value and guards `getSurfaceKindProperty`
   * with try/catch (the global property service may not be initialized yet).
   */
  private syncSurfaceKindSubscription(): void {
    const surface = this.surfaceContext?.getSelectedSurface() ?? null;
    const identifier = surface?.identifier ?? null;

    // Nothing changed — keep the existing subscription.
    if (identifier === this.subscribedSurfaceIdentifier) return;

    this.surfaceKindSubscription?.unsubscribe();
    this.surfaceKindSubscription = null;
    this.subscribedSurfaceIdentifier = identifier;

    if (!surface || !identifier) {
      // No selection — fall back to SURFACE so the getter still resolves.
      if (this.currentSurfaceKind !== SurfaceKind.SURFACE) {
        this.currentSurfaceKind = SurfaceKind.SURFACE;
        this.onPipelineConfigChanged?.();
      }
      return;
    }

    // Seed from the surface's current kind, then subscribe for live flips.
    if (surface.surfaceKind !== this.currentSurfaceKind) {
      this.currentSurfaceKind = surface.surfaceKind;
      this.onPipelineConfigChanged?.();
    }

    try {
      const kindProperty = getSurfaceKindProperty(identifier);
      this.surfaceKindSubscription = kindProperty.subscribe((kind: SurfaceKind) => {
        if (kind !== this.currentSurfaceKind) {
          this.currentSurfaceKind = kind;
          this.onPipelineConfigChanged?.();
        }
      });
    } catch (error) {
      // Global property service not initialized yet; the getter falls back to
      // the seeded value above.
      console.warn('Failed to subscribe to surface kind property:', error);
    }
  }

  /**
   * Polygon pipeline JSON path, resolved dynamically from the selected
   * surface's kind. SECTION surfaces want the section pipeline; everything
   * else (SURFACE) uses the surface pipeline. Implemented as a getter so it
   * stays correct even if the host never wires `setPipelineConfigChangedHandler`.
   *
   * NOTE: the surface MASK pipeline (`surface-mask-pipeline-config.json`) is
   * the FloodSegmentation strategy's config, NOT the polygon pipeline, so it's
   * deliberately not referenced here.
   */
  get polygonPipelineConfigPath(): string | null {
    return this.currentSurfaceKind === SurfaceKind.SECTION
      ? SurfaceKindAdapter.SECTION_PIPELINE_CONFIG
      : SurfaceKindAdapter.SURFACE_PIPELINE_CONFIG;
  }

  /**
   * Register the host/subtool callback fired when the surface kind flips, so
   * the worker can reload the (now-different) polygon pipeline. Pass null to
   * clear it (on teardown).
   */
  setPipelineConfigChangedHandler(handler: (() => void) | null): void {
    this.onPipelineConfigChanged = handler;
  }

  /**
   * Surfaces use the surface-mask flow; the old AutoSurfaceTool named no
   * separate surface depth pipeline beyond the mask config (which is the
   * segmentation-strategy config, not a depth pipeline). Return null so the
   * WorkerManager falls back to its default line-tool depth config.
   */
  readonly depthPipelineConfigPath: string | null = null;

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D_SURFACE, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D_SURFACE.id,
    };
  }

  /**
   * Hit-test SURFACE AreaAnnotations under the cursor. Iterates the scene's
   * measurements, accepts plain `AreaAnnotation`s (surfaces) with >= 3 points —
   * `OpeningAnnotation` (door/window) is an AreaAnnotation subclass, so it's
   * explicitly excluded. Each annotation's points are projected to pixel space
   * via the active camera and tested with an even-odd point-in-polygon, exactly
   * like the door/window adapter's hover hit-tester.
   */
  hitTestEditableTarget(ctx: HitTestContext): EditableShape | null {
    const { viewer, mouse } = ctx;
    for (const m of viewer.scene.measurements) {
      if (!(m instanceof AreaAnnotation)) continue;
      // OpeningAnnotation extends AreaAnnotation but is a door/window, not a
      // surface — its kind is 'door_window'.
      if (m instanceof OpeningAnnotation) continue;
      if (m.getKind() !== 'surface') continue;
      if (m.points.length < 3) continue;
      const projected: { x: number; y: number }[] = [];
      let allOnScreen = true;
      for (const p of m.points) {
        const ndc = p.position.clone().project(viewer.scene.getActiveCamera());
        if (ndc.z < -1 || ndc.z > 1) {
          allOnScreen = false;
          break;
        }
        const sx = ((ndc.x + 1) / 2) * viewer.renderer.domElement.clientWidth;
        const sy = ((1 - ndc.y) / 2) * viewer.renderer.domElement.clientHeight;
        projected.push({ x: sx, y: sy });
      }
      if (!allOnScreen) continue;
      if (this.pointInPolygon2D(mouse, projected)) return m as unknown as EditableShape;
    }
    return null;
  }

  isMouseOverEditableShape(ctx: HitTestContext): boolean {
    return this.hitTestEditableTarget(ctx) !== null;
  }

  /**
   * The MeasureSubTool engine may only edit surface polygons: plain
   * `AreaAnnotation`s, with openings excluded — the same discrimination
   * `hitTestEditableTarget` uses.
   */
  canEditAnnotation(annotation: EditableAnnotation): boolean {
    return annotation instanceof AreaAnnotation && !(annotation instanceof OpeningAnnotation);
  }

  /** Even-odd point-in-polygon test in pixel space. */
  private pointInPolygon2D(p: { x: number; y: number }, poly: { x: number; y: number }[]): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x, yi = poly[i].y;
      const xj = poly[j].x, yj = poly[j].y;
      if (yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  shouldRunSegmentationOnEnter(_shape: EditableShape): boolean {
    return true;
  }

  configureViewForShape(_shape: EditableShape, _viewer: Viewer): void {
    // no-op — the SurfaceHostTool / SurfaceToolBase already aligned the camera
    // to the surface plane (alignViewToSurface) before nesting this adapter.
  }

  restoreViewAfterShape(_shape: EditableShape, _viewer: Viewer): void {
    // no-op — the SurfaceHostTool tears down the surface focus on disable /
    // surface change / Esc / Enter.
  }

  /**
   * Materialize a new surface from a confirmed polygon. Reuses the exact
   * persistence path the old `AutoSurfaceTool` confirm flow used: build a
   * measure-like object (`AREA` type, `auto_surface_*` identifier, world points
   * with a null pointCloudId) and dispatch `measurement_finish` with
   * `skipSelection: true` so the annotation isn't auto-selected after creation.
   */
  commitNew(viewer: Viewer, polygon: THREE.Vector3[], _context: CommitContext): void {
    if (polygon.length < 3) return;
    const measure = {
      identifier: `auto_surface_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      type: 'AREA',
      points: polygon.map((p) => ({ position: p.clone(), pointCloudId: null })),
    };
    viewer.scene.dispatchEvent({
      type: 'measurement_finish',
      value: { measure, skipSelection: true },
    });
  }

  /**
   * Persist an edit to an existing surface AreaAnnotation. `exitEditMode(true,
   * polygon)` already rebuilt the in-scene visuals via `setPoints`, but that
   * does NOT write the reactive `area_points` property, so the edit must be
   * round-tripped through the same persistence path `commitNew` uses: a
   * `measurement_finish` carrying the existing identifier and the edited
   * polygon.
   *
   * Surfaces are `AreaAnnotation`s that load their geometry from
   * `getAreaPointsProperty(id)`. So an edit must (1) apply the polygon to the
   * live annotation (visual) and (2) write that property (persist). It must NOT
   * re-dispatch `measurement_finish` — that handler always *creates* a new
   * annotation, producing a duplicate with the same identifier (and a duplicate
   * React key) instead of updating the existing surface.
   */
  commitEdit(_viewer: Viewer, shape: EditableShape, polygon: THREE.Vector3[]): void {
    if (polygon.length < 3) return;
    const identifier = (shape as unknown as { identifier?: string }).identifier;
    if (!identifier) return;
    // Apply to the live annotation (updates the in-scene visual). On undo/redo
    // restore the caller passes the restored polygon, so this also moves the
    // shape back; on live edits it re-applies the already-current geometry.
    if (shape instanceof AreaAnnotation) {
      shape.setPoints(polygon.map((p) => ({ position: p.clone(), pointCloudId: undefined })));
    }
    // Persist to the surface's own store (the AreaAnnotation subscribes to it).
    getAreaPointsProperty(identifier).value = polygon.map((p) => ({
      position: { x: p.x, y: p.y, z: p.z },
      pointCloudId: null,
    }));
  }

  /**
   * Project each vertex onto the surface plane that the SurfaceHostTool
   * installed on `viewer.viewerMode.associatedPlane`. Collapses the small
   * perpendicular noise the segmentation + manual-prompt pipeline introduces.
   * When no plane is installed (defensive — surface drawing shouldn't run
   * without a focused surface), vertices pass through unchanged.
   */
  snapPolygonToBasePlane(vertices: THREE.Vector3[], context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] {
    const plane = context.viewer.viewerMode.associatedPlane;
    if (!plane) return vertices;
    const origin = plane.position;
    const normal = plane.getNormal().clone().normalize();
    return vertices.map((v) => {
      const offset = v.clone().sub(origin);
      const perp = offset.dot(normal);
      return v.clone().addScaledVector(normal, -perp);
    });
  }

  /**
   * Build the in-plane frame for the focused surface plane. Local axes: u in
   * the plane (derived from the plane normal × world +Z), v completes a
   * right-handed frame, normal = surface normal. With this frame installed, the
   * XY-hardcoded snap modules run their existing math in plane-local
   * coordinates. Returning null (no associatedPlane) falls back to world XY.
   *
   * The math is plane-agnostic (origin + normal + u/v frame), so it works for
   * any surface orientation, mirroring the door/window adapter's frame.
   */
  getBasePlaneFrame(context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): { origin: THREE.Vector3; u: THREE.Vector3; v: THREE.Vector3; normal: THREE.Vector3 } | null {
    const plane = context.viewer.viewerMode.associatedPlane;
    if (!plane) return null;
    const normal = plane.getNormal().clone().normalize();
    const worldZ = new THREE.Vector3(0, 0, 1);
    const u = new THREE.Vector3().crossVectors(normal, worldZ).normalize();
    // Degenerate (normal parallel to +Z, i.e. a horizontal surface) — fall
    // back to world +X so the frame stays defined.
    if (u.lengthSq() < 1e-9) u.set(1, 0, 0);
    const v = new THREE.Vector3().crossVectors(normal, u).normalize();
    return { origin: plane.position.clone(), u, v, normal };
  }

  /**
   * Eight world-space corners of an oriented box around the surface slab so the
   * screenshot encoder derives a tight eye-Z range (the surface camera looks
   * down a non-Z axis, so a world-Z slab is meaningless). Sized from the
   * surface plane's in-plane half-extents (its `radius`, doubled for the box
   * extent) with a thin slab along the normal. Falls back to a generous
   * 5m × 5m × 0.5m slab when the plane carries no radius. Returns null
   * defensively when no plane is installed.
   */
  getCaptureBoxCorners(context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] | null {
    const frame = this.getBasePlaneFrame(context);
    if (!frame) return null;
    const plane = context.viewer.viewerMode.associatedPlane;
    // PointoramaSurface.radius is a half-extent (defaults to 1m); double it for
    // a comfortable in-plane box. Surfaces carry no wall-thickness marker, so a
    // 0.5m normal slab + a 5m fallback covers the common case.
    const radius = (plane as unknown as { radius?: number }).radius;
    const halfU = radius && radius > 0 ? radius * 2 : 5;
    const halfV = halfU;
    const halfN = 0.5;
    const corners: THREE.Vector3[] = [];
    for (const su of [-1, 1]) {
      for (const sv of [-1, 1]) {
        for (const sn of [-1, 1]) {
          const c = frame.origin.clone()
            .addScaledVector(frame.u, su * halfU)
            .addScaledVector(frame.v, sv * halfV)
            .addScaledVector(frame.normal, sn * halfN);
          corners.push(c);
        }
      }
    }
    return corners;
  }
}
